import liff from '@line/liff';

const LIFF_ID = import.meta.env.VITE_LIFF_ID as string | undefined;

export type LiffState =
  /** No `VITE_LIFF_ID` configured — local development against the API alone. */
  | { kind: 'disabled' }
  /** Initialised and signed in; the server session has been established. */
  | { kind: 'ready'; inClient: boolean }
  /** Initialised but LINE has no session; `liff.login()` has been kicked off. */
  | { kind: 'redirecting' }
  | { kind: 'error'; message: string };

/**
 * Brings up LIFF and trades its ID token for an app session.
 *
 * Inside the LINE app the person is already authenticated to LINE, so there is
 * no login screen at all: `liff.init()` resolves with an ID token, one call to
 * `/auth/sign-in/liff` verifies it server-side, and the session cookie is set.
 * In a desktop browser `liff.login()` runs the same LINE Login redirect and
 * comes back to this page, after which the identical exchange happens.
 */
export async function initLiff(): Promise<LiffState> {
  if (!LIFF_ID) return { kind: 'disabled' };

  try {
    await liff.init({ liffId: LIFF_ID });
  } catch (error) {
    return { kind: 'error', message: describe(error) };
  }

  if (!liff.isLoggedIn()) {
    // Sends the browser to LINE and back; nothing after this runs.
    liff.login({ redirectUri: window.location.href });
    return { kind: 'redirecting' };
  }

  const idToken = liff.getIDToken();
  if (!idToken) {
    return { kind: 'error', message: 'LINE did not return an ID token' };
  }

  try {
    await exchangeIdToken(idToken);
  } catch (error) {
    return { kind: 'error', message: describe(error) };
  }

  return { kind: 'ready', inClient: liff.isInClient() };
}

/** Trades a LINE ID token for a Better Auth session cookie. */
async function exchangeIdToken(idToken: string): Promise<void> {
  const response = await fetch('/auth/sign-in/liff', {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ idToken }),
  });

  if (!response.ok) {
    throw new Error(`LINE sign-in failed (${response.status})`);
  }
}

/** Closes the LIFF window — only meaningful inside the LINE app. */
export function closeLiff(): void {
  if (LIFF_ID && liff.isInClient()) liff.closeWindow();
}

function describe(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}
